/**
 * Data sources & references shown in the resources panel.
 *
 * TODO(content): confirm final copy/URLs with product; this mirrors the
 * "Data Sources & References" handoff table.
 */

export interface Resource {
  title: string;
  category: string;
  description: string;
  year: string;
  url: string;
}

export const RESOURCE_PANEL_TITLE =
  'Opportunity Map: Data Sources & References';
export const RESOURCE_PANEL_SUBTITLE =
  'Trump Accounts (IRC Section 530A) · Lafayette Square / Worker Solutions · 2026';

export const RESOURCES: readonly Resource[] = [
  {
    title: 'US Census Bureau — American Community Survey (ACS 5-Year)',
    category: 'Census Data',
    description:
      'Primary data source for 9 of 12 indicators. Tables used: B19013 (median household income), B01001 (children by age), B25077 (median home values), B23008 (parental employment), B11012 (single-parent households), B17010 (families in poverty with children), B19083 (Gini index), B25003 (homeownership), B25070/B25091 (housing cost burden). 2024 release.',
    year: '2024',
    url: 'https://census.gov/programs-surveys/acs',
  },
  {
    title: 'DiversityDataKids.org — Child Opportunity Index 3.0 (COI)',
    category: 'Opportunity Index',
    description:
      'County-level composite index measuring neighborhood opportunity for children across 29 indicators in three domains: education (school quality, early childhood access), health and environment (pollution, green space), and social/economic conditions (poverty, employment). National percentile score 1–100. Aggregated to MSA and state via population-weighted mean.',
    year: '2023',
    url: 'https://diversitydatakids.org/data-item/child-opportunity-index-coi-30',
  },
  {
    title: 'Urban Institute — Debt in America',
    category: 'Financial Distress',
    description:
      'County and state-level share of adults with any debt in collections (180+ days past due, transferred to collections agency). Measures active financial distress beyond income poverty. Direct state-level file used for state score; county file used for county and MSA scores.',
    year: '2025',
    url: 'https://datacatalog.urban.org/dataset/debt-america-2025',
  },
  {
    title: 'Opportunity Atlas — Chetty, Friedman, Hendren, Jones, Porter',
    category: 'Mobility Research',
    description:
      'Intergenerational mobility data for every US county based on earnings records of approximately 20 million children born 1978–1983, linked to tax records as adults. Variable used: kfr_pooled_pooled_p25 — average income percentile at age 35 for children from the bottom income quintile.',
    year: '2018',
    url: 'https://opportunityatlas.org',
  },
  {
    title: 'Sallie Mae — How America Saves for College',
    category: 'Savings Behavior',
    description:
      'Annual national survey of 529 college savings plan adoption rates by household income bracket. Used to model the geography-specific 529 adoption rate indicator and deadweight adjustment in the ROI calculation. Income tiers: <$35K → 2%, $35–75K → 9%, $75–100K → 22%, >$100K → 37%.',
    year: '2024',
    url: 'https://salliemae.com/college-planning/how-america-saves-for-college',
  },
  {
    title: 'Aspen Institute — Essential Wealth: A New Benchmark for Financial Security',
    category: 'Wealth Thresholds',
    description:
      'Defines three financial milestones by age group — Asset Poverty Line, Emergent Wealth, and Essential Wealth. Used to construct the Moving the Savings Needle metric. Geography-specific thresholds derived from local median home values: Emergent Wealth = (median home price × 3.5%) + $2,000; Essential Wealth = (median home price × 10%) + (MHI × 0.75 / 52 × 6 weeks).',
    year: 'June 2026',
    url: 'https://aspeninstitute.org/publications/essential-wealth-report-benchmark-for-financial-security',
  },
  {
    title: 'FDIC — National Survey of Unbanked and Underbanked Households',
    category: 'Financial Inclusion',
    description:
      'Biennial household survey measuring the share of US households without a bank or credit union account. Available at national, state, and large-MSA level. Dashboard metric only — not included in composite score. Key variables: hunbnk (1=unbanked), hsupresp==1 (valid respondents), hhsupwgt (household weight). Source file: hh2023_analys.csv.',
    year: '2023',
    url: 'https://fdic.gov/household-survey',
  },
  {
    title: 'Chetty & Hendren — Neighborhoods and Intergenerational Mobility',
    category: 'Community ROI',
    description:
      'Peer-reviewed research in the Quarterly Journal of Economics measuring long-term earnings effects of childhood neighborhood quality. Provides the $0.65 tax revenue multiplier used in the community ROI calculation: $0.65 in lifetime tax revenue per $1 in account balance at age 18.',
    year: '2018',
    url: 'https://opportunityinsights.org/paper/neighborhoodsincome',
  },
  {
    title: 'Hendren & Sprung-Keyser — A Unified Welfare Analysis of Government Policies',
    category: 'Community ROI',
    description:
      'Harvard research measuring the Marginal Value of Public Funds (MVPF) for childhood investments — the government fiscal return per dollar spent. Provides the $1.78 health and fiscal return multiplier in the community ROI calculation.',
    year: '2020',
    url: 'https://nber.org/papers/w26881',
  },
  {
    title: 'Heckman et al. — The Lifecycle Benefits of an Influential Early Childhood Program',
    category: 'Community ROI',
    description:
      'Research by Nobel Laureate James Heckman demonstrating a 13% annual social return on high-quality early childhood investments in disadvantaged children, based on the ABC/CARE randomized control trial in North Carolina. Referenced in the executive ROI presentation method.',
    year: '2016',
    url: 'https://heckmanequation.org',
  },
  {
    title: 'Social Value International — SROI Standard',
    category: 'ROI Methodology',
    description:
      'International standard for measuring Social Return on Investment. Defines the four adjustments applied in the net philanthropic ROI calculation: deadweight (what would happen anyway), attribution (share of outcome attributable to the intervention), displacement (offset to other communities), and drop-off (outcome decay over time).',
    year: '2019',
    url: 'https://socialvalueint.org',
  },
  {
    title: 'IRC Section 530A — Trump Accounts',
    category: 'Statutory Basis',
    description:
      'Statutory basis for the Trump Account program. Defines eligibility (children born 2025–2028), the $1,000 automatic federal seed deposit, the 7% annual return cap (S&P 500 minus 0.10% expense cap), and permitted contribution types including family, employer, and third-party philanthropic contributions.',
    year: '2025',
    url: 'https://congress.gov',
  },
  {
    title: 'BEA — Regional Price Parities',
    category: 'Cost of Living',
    description:
      'State and MSA-level cost of living indices published by the Bureau of Economic Analysis. US average = 100. Used to adjust Aspen Essential Wealth thresholds to reflect local purchasing power where applicable.',
    year: 'Annual',
    url: 'https://bea.gov/data/prices-inflation/regional-price-parities-state-and-metro-area',
  },
];
